import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { Reveal } from "@/components/reveal";
import { Section } from "@/components/section";
import { CtaBand } from "@/components/cta-band";
import { ResponsiveImg } from "@/components/responsive-img";
import { lcpPreload, imgSizes } from "@/lib/images";

export const Route = createFileRoute("/our-story")({
  head: () => ({
    meta: [
      { title: "Our Story | Magnolia House Custom Picture Framing" },
      {
        name: "description",
        content:
          "Shirley Hunsberger founded Magnolia House in Fuquay-Varina in 1989. Thirty-five years of certified custom framing, from the historic Ballentine-Spence House to 901 Jacobean Court.",
      },
      {
        property: "og:title",
        content: "Our Story | Magnolia House Custom Picture Framing",
      },
      {
        property: "og:description",
        content:
          "A lifelong passion for the visual arts, three and a half decades in one small North Carolina town.",
      },
    ],
    links: [lcpPreload("shop-street")],
  }),
  component: StoryPage,
});

const CHAPTERS = [
  {
    year: "Before 1989",
    title: "The Framer",
    image: "story-old-sign",
    body: "Born in England and raising three daughters in the United States, Shirley started working in picture framing when her youngest began school. She soon launched her own business, calling herself simply The Framer.",
  },
  {
    year: "1989",
    title: "Magnolia House",
    image: "story-ballentine",
    body: "When the family relocated to Fuquay-Varina, she rebranded as Magnolia House and ran the shop out of her home at 109 East Spring Street — the Ballentine-Spence House, which she helped place on the register of historic places. It remains a landmark in Fuquay's Historic District.",
  },
  {
    year: "2001 – 2024",
    title: "218 South Main Street",
    image: "shop-exterior",
    body: "For twenty-four years Magnolia House was a fixture on South Main Street, framing diplomas, jerseys, flags and heirlooms for generations of the same families, and supporting local artists through community events and organizations.",
  },
  {
    year: "2025",
    title: "901 Jacobean Court",
    image: "shop-street",
    body: "Early in 2025 Shirley moved the business to a space built around unhurried client appointments — the same certified work, with more room to design your piece properly and more flexible times to meet.",
  },
];

function StoryPage() {
  return (
    <>
      <PageHero
        eyebrow="Our story"
        title={
          <>
            Thirty-five years,
            <br />
            <span className="amber-text italic">one town.</span>
          </>
        }
        lead="Magnolia House has been owner-operated by Shirley Hunsberger since 1989. Here is how a lifelong love of the visual arts became a Fuquay-Varina institution."
        image="shop-street"
        alt="The Magnolia House shop sign on South Main Street"
      />

      <Section>
        <div className="relative grid gap-24">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.year}>
              <article
                className={`grid items-center gap-12 md:grid-cols-2 ${
                  i % 2 ? "md:[direction:rtl]" : ""
                }`}
              >
                <div className="plate [direction:ltr]">
                  <ResponsiveImg
                    name={c.image}
                    sizes={imgSizes.half}
                    alt={c.title}
                    loading="lazy"
                    decoding="async"
                    className={`aspect-[4/3] w-full ${
                      c.image === "story-old-sign"
                        ? "object-contain bg-[oklch(0.88_0.01_80)] p-6"
                        : "object-cover"
                    }`}
                  />
                </div>
                <div className="min-w-0 [direction:ltr]">
                  <p className="eyebrow">{c.year}</p>
                  <h2 className="display mt-4 text-4xl sm:text-5xl">{c.title}</h2>
                  <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-muted-foreground">
                    {c.body}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="mist">
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="eyebrow">Then and now</p>
          <p className="display mt-6 text-3xl leading-snug sm:text-4xl">
            “The certification matters, but what keeps people coming back is simpler — someone who
            cares about your piece as much as you do.”
          </p>
          <p className="mt-6 text-[11px] uppercase tracking-[0.26em] text-muted-foreground">
            Shirley Hunsberger, CPF
          </p>
        </Reveal>
      </Section>

      <CtaBand />
    </>
  );
}
